import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useActivationChecklist } from '../contexts/ActivationChecklistContext';
import {
  isTrialDemarrageExemptPath,
  shouldForceTrialDemarrage,
} from '../utils/trialDemarrageRedirect';
import { useMobileViewport } from '../hooks/useMobileViewport';

/* Coupe-circuit anti-boucle : si une autre page renvoie l'utilisateur vers une route
   non exemptée pendant que le garde force /demarrage, les deux redirections se
   répondent à l'infini et l'app se fige. Au-delà de quelques redirections en
   quelques secondes, on laisse passer : le garde n'est qu'un aiguillage d'onboarding,
   les vrais accès restent protégés par PrivateProRoute et les règles Firestore. */
const REDIRECT_BURST_WINDOW_MS = 4000;
const REDIRECT_BURST_LIMIT = 6;
let recentRedirectTimestamps: number[] = [];
let redirectBreakerTripped = false;

function registerRedirectAndCheckBreaker(): boolean {
  if (redirectBreakerTripped) return true;

  const now = Date.now();
  recentRedirectTimestamps = recentRedirectTimestamps.filter(
    (timestamp) => now - timestamp < REDIRECT_BURST_WINDOW_MS,
  );
  recentRedirectTimestamps.push(now);

  if (recentRedirectTimestamps.length > REDIRECT_BURST_LIMIT) {
    redirectBreakerTripped = true;
    console.warn('[TrialDemarrageGuard] Boucle de redirection détectée vers /demarrage — garde désactivé.');
  }

  return redirectBreakerTripped;
}

/**
 * Redirige vers /demarrage uniquement quand le mur démo est actif
 * (`onboardingDemoLocked: true`), pas pour tout trial sans compte connecté.
 * Avant le verrou, l'utilisateur peut naviguer librement (sidebar, démo, etc.).
 */
export function TrialDemarrageGuard({ children }: { children: React.ReactNode }) {
  const { loading, hasActiveTrialOnboarding, onboardingDemoLocked } = useAuth();
  const { checklistState } = useActivationChecklist();
  const location = useLocation();
  const isMobileViewport = useMobileViewport();

  const isLockResolved = onboardingDemoLocked !== null;
  const isDemoLocked = onboardingDemoLocked === true;

  if (loading || !isLockResolved) {
    return <>{children}</>;
  }

  if (
    shouldForceTrialDemarrage(hasActiveTrialOnboarding, checklistState.synchronisation, isMobileViewport)
    && isDemoLocked
    && !isTrialDemarrageExemptPath(location.pathname, location.search, { isDemoLocked })
  ) {
    if (!registerRedirectAndCheckBreaker()) {
      return <Navigate to="/demarrage" replace />;
    }
  }

  return <>{children}</>;
}
